<template>
  <transition name="cookie-slide-up">
    <section
      v-if="bannerOpen"
      class="cookie-banner"
      role="dialog"
      aria-live="polite"
      aria-label="Privacy settings"
    >
      <div class="cookie-banner__body">
        <p class="cookie-banner__title">Privacy choices</p>
        <p class="cookie-banner__text">
          We use necessary cookies to keep the site secure and working. With your
          permission, we can also use optional analytics to understand how students
          use Engram and improve learning experiences.
        </p>
        <p class="cookie-banner__meta">Necessary cookies are always on. Analytics is optional.</p>
        <p v-if="analyticsEnabled" class="cookie-banner__status">
          Analytics is currently
          <strong>{{ hasAnalyticsConsent() ? "enabled" : "disabled" }}</strong>.
        </p>
      </div>

      <div class="cookie-banner__actions">
        <button
          v-if="analyticsEnabled"
          type="button"
          class="cookie-btn cookie-btn--primary"
          @click="onAccept"
        >
          Accept analytics
        </button>
        <button
          type="button"
          class="cookie-btn cookie-btn--secondary"
          @click="onDecline"
        >
          {{ analyticsEnabled ? "Decline analytics" : "Got it" }}
        </button>
        <router-link class="cookie-link" :to="privacyPolicyPath">Learn more</router-link>
        <button
          v-if="consent"
          type="button"
          class="cookie-link cookie-link--button"
          @click="closeBanner"
        >
          Close
        </button>
      </div>
    </section>
  </transition>
</template>

<script>
import { computed } from "vue";
import { useCookieConsent } from "@/composables/useCookieConsent";
import { initAnalyticsIfConsented } from "@/services/analyticsService";

export default {
  name: "CookieConsentBanner",
  props: {
    privacyPolicyPath: {
      type: String,
      default: "/privacypolicy"
    }
  },
  setup() {
    const {
      bannerOpen,
      consent,
      acceptAnalytics,
      declineAnalytics,
      hasAnalyticsConsent,
      isAnalyticsFeatureEnabled,
      closeBanner
    } = useCookieConsent();

    const analyticsEnabled = computed(() => isAnalyticsFeatureEnabled());

    const onAccept = () => {
      acceptAnalytics();
      initAnalyticsIfConsented();
    };

    const onDecline = () => {
      declineAnalytics();
    };

    return {
      analyticsEnabled,
      bannerOpen,
      closeBanner,
      consent,
      hasAnalyticsConsent,
      onAccept,
      onDecline
    };
  }
};
</script>

<style scoped>
.cookie-banner {
  position: fixed;
  left: 1rem;
  right: 1rem;
  bottom: 1rem;
  z-index: 10040;
  margin: 0 auto;
  width: min(900px, calc(100% - 2rem));
  border-radius: 14px;
  border: 1px solid #ded8ef;
  background: #ffffff;
  box-shadow: 0 20px 50px rgba(17, 24, 39, 0.16);
  padding: 1rem;
}

.cookie-banner__title {
  margin: 0 0 0.35rem;
  font-size: 1rem;
  font-weight: 800;
  color: #1f2937;
}

.cookie-banner__text {
  margin: 0;
  font-size: 0.94rem;
  color: #374151;
  line-height: 1.45;
}

.cookie-banner__meta {
  margin: 0.45rem 0 0;
  font-size: 0.84rem;
  color: #6b7280;
}

.cookie-banner__status {
  margin: 0.4rem 0 0;
  font-size: 0.83rem;
  color: #4b5563;
}

.cookie-banner__actions {
  margin-top: 0.85rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.55rem;
  align-items: center;
}

.cookie-btn {
  border-radius: 999px;
  padding: 0.45rem 0.85rem;
  font-size: 0.84rem;
  font-weight: 700;
  border: 1px solid transparent;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.cookie-btn:hover {
  transform: translateY(-1px);
}

.cookie-btn--primary {
  background: #8c30f5;
  color: #fff;
  border-color: #8c30f5;
  box-shadow: 0 4px 12px rgba(140, 48, 245, 0.25);
}

.cookie-btn--secondary {
  background: #ffffff;
  color: #374151;
  border-color: #d1d5db;
}

.cookie-link {
  color: #6d28d9;
  font-weight: 700;
  text-decoration: none;
  font-size: 0.84rem;
}

.cookie-link:hover {
  text-decoration: underline;
}

.cookie-link--button {
  background: transparent;
  border: 0;
  padding: 0;
  cursor: pointer;
}

.cookie-slide-up-enter-active,
.cookie-slide-up-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.cookie-slide-up-enter-from,
.cookie-slide-up-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

@media (max-width: 576px) {
  .cookie-banner {
    left: 0.6rem;
    right: 0.6rem;
    bottom: 0.6rem;
    width: auto;
    padding: 0.85rem;
  }

  .cookie-banner__text {
    font-size: 0.88rem;
  }
}
</style>
